import * as React from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import DashboardLayout from '../../layouts/DashboardLayout';
import ActionsPanel from '../../organisms/ActionsPanel';
import useTablePage from '../../hooks/useTablePage';
import {
  ModalDefaultDraggable,
  ModalDeleteConfirmation,
} from '../../molecules';
import Table from '../../organisms/Table';
import tableColumnsFormatter from '../../utils/tableColumnsFormatter';
import FranchiseForm from '../../forms/FranchiseForm';
import ModalInfo from '../../molecules/ModalInfo';
import { convertTableFranchiseType } from '../../utils/convertTableValues';
import { ROUTES } from '../../constants/routes';
import { setFranchiseInfo } from '../../redux/ducks/franchiseSettings';
import {
  getFranchiseList,
  deleteFranchise,
  createFranchise,
  updateFranchise,
  FranchiseType,
} from '../../api/franchise';
import useUserRole from '../../hooks/useUserRole';

const FranchisePage = () => {
  const {
    setSearch,
    handleAdd,
    modalTitle,
    isModalOpen,
    search,
    setSelectedItem,
    selectedItem,
    tableManuallyUpdate,
    handleDelete,
    handleCloseModal,
    deleteById,
    modalSuccess,
    values,
    modalError,
    errors,
    handleEdit,
    modalErrorTitle,
    createOrUpdate,
    handleCloseModalInfo,
  } = useTablePage<FranchiseType>({
    createEndpoint: createFranchise,
    updateEndpoint: updateFranchise,
    modalCreateTitle: 'Add Branch',
    modalUpdateTitle: 'Update Branch',
    deleteEndpoint: deleteFranchise,
  });
  const { isSuperAdmin } = useUserRole();

  const history = useHistory();
  const dispatch = useDispatch();

  const columns = column => {
    if (
      [
        'isDeleted',
        'contactPerson',
        'note',
        'street',
        'city',
        'state',
        'zipCode',
        'toDate',
        'fromDate',
        'franchiseStripeSetting',
        'qrcFilename',
        'qrc',
        'qrcBase64',
      ].includes(column)
    ) {
      return null;
    }

    switch (column) {
      case 'id':
        return 'Branch ID';
      case 'phoneNumber':
        return 'Phone';
      case 'name':
        return 'Branch Name';
        case 'franchiseType':
          return 'Branch Type';
          case 'franchiseLevel':
            return 'Branch Level';
      default:
        return tableColumnsFormatter(column);
    }
  };

  const columnsOrder = [
    'id',
    'franchiseType',
    'name',
    'address',
    'firstName',
    'lastName',
    'email',
    'phoneNumber',
    'franchiseLevel',
    'yearlyGoal',
  ];

  const pushToSettingsPage = () => {
    if (values) {
      dispatch(setFranchiseInfo(values));
      return history.push(
        ROUTES.FRANCHISE_CONFIGURATION_JURISDICTION,
      );
    }
    return handleCloseModal();
  };

  const handleCloseForm = () => {
    if (!modalError && !modalSuccess) {
      handleCloseModal();
    }
  };

  return (
    <DashboardLayout>
      <PageWrapper>
        <ActionsPanel
          onAdd={isSuperAdmin && handleAdd}
          onSearch={setSearch}
        />

      <Container>
        <Table
          customSearch
          searchQuery={search}
          columns={columns}
          rows={getFranchiseList}
          onDelete={isSuperAdmin && handleDelete}
          onEdit={handleEdit}
          initialSortBy={[{ id: 'name', desc: false }]}
          manuallyUpdate={tableManuallyUpdate}
          columnsOrder={columnsOrder}
          formatValue={convertTableFranchiseType}
        />
      </Container>

      {isModalOpen && (
        <ModalDefaultDraggable
          onClose={handleCloseForm}
          title={modalTitle}
          wide
        >
          <FranchiseForm
            initialValues={values}
            initialErrors={errors}
            modalError={modalErrorTitle.length > 0}
            onSubmit={createOrUpdate}
            pushToSettingsPage={pushToSettingsPage}
          />
        </ModalDefaultDraggable>
      )}

      {selectedItem && (
        <ModalDeleteConfirmation
          onCancel={() => setSelectedItem(null)}
          onConfirm={() => deleteById(selectedItem.id)}
        >
          <strong>{selectedItem.name}</strong>
        </ModalDeleteConfirmation>
      )}

      {(modalSuccess || modalError) && (
        <ModalInfo
          timeToClose={modalError ? 0 : 5000}
          isError={modalError}
          onClose={handleCloseModalInfo}
          errorMsg={modalErrorTitle}
        />
      )}
      </PageWrapper>
    </DashboardLayout>
  );
};

const PageWrapper = styled.div`
  width: 100%;
  padding: 20px 16px 0;
  box-sizing: border-box;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont,
    'Helvetica Neue', Arial, Roboto, sans-serif,
    'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol';

  @media (max-width: 840px) {
    padding: 12px 12px 0;
  }
`;

const Container = styled.div`
  margin: 20px 0 0 0;
`;

export default FranchisePage;
